<template>
    <div class="documents-tab p-3">
        <b-alert show variant="info" class="d-flex align-items-center mb-4 border-0 shadow-sm" style="background-color: #e8f4fd; color: #0c5460;">
            <i class="ri-information-line mr-3" style="font-size: 1.8rem;"></i>
            <div class="font-size-14">
                <strong class="d-block mb-1">{{ $t('please_upload_required_documents') }}</strong>
                <span>{{ $t('document_upload_format_info') }}</span>
            </div>
        </b-alert>

        <div class="mb-3 h5 font-weight-bold">
            {{ $t('documents') }}
        </div>

        <div class="bg-white rounded shadow-sm border p-4 mb-4">
            <div
                v-for="(doc, index) in requiredDocuments"
                :key="doc.code"
                class="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom last-border-0"
            >
                <div class="font-weight-bold text-dark">
                    {{ doc.name }}<span v-if="doc.required" class="text-danger ml-1">*</span>
                </div>
                <div class="d-flex align-items-center">
                    <div v-if="doc.alreadyUploaded && doc.files.length === 0" class="mr-3 text-success small font-weight-bold d-flex align-items-center">
                        <i class="ri-checkbox-circle-fill mr-1 font-size-18"></i>
                        {{ $t('already_uploaded') }}
                    </div>
                    <div v-else-if="doc.files.length > 0" class="mr-3 text-success small font-weight-bold d-flex align-items-center">
                        <i class="ri-checkbox-circle-fill mr-1 font-size-18"></i>
                        <span v-if="doc.files.length === 1">{{ doc.files[0].name }}</span>
                        <span v-else>{{ doc.files.length }} {{ $t('files_selected') }}</span>
                    </div>
                    <input
                        type="file"
                        :id="`file-input-${index}`"
                        v-show="false"
                        multiple
                        accept=".pdf,.jpg,.png,.jpeg"
                        @change="onFileChange($event, doc)"
                    />
                    <label :for="`file-input-${index}`" class="mb-0 cursor-pointer d-flex align-items-center">
                        <b-button
                            :variant="(doc.alreadyUploaded || doc.files.length > 0) ? 'success' : 'outline-dark'"
                            size="sm"
                            class="px-3 py-1 font-weight-bold rounded shadow-sm d-flex align-items-center"
                            style="pointer-events: none;"
                        >
                            {{ $t('upload') }}
                            <i class="ri-add-line ml-1 font-size-16"></i>
                        </b-button>
                    </label>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-center mt-4">
            <b-button variant="primary" @click="submitAndNext" class="px-5 font-weight-bold shadow-sm" :disabled="loading">
                <b-spinner small v-if="loading" class="mr-2" />
                {{ $t('next') }} <i class="ri-arrow-right-line"></i>
            </b-button>
        </div>

        <CommonModal ref="successModal" size="md" :onHideOnlyX="true" :closeBtn="false" @bHideModalHeaderClose="goToSummary">
            <template v-slot:CommonModalTitle>
                <div class="d-flex align-items-center" style="color: #00b0ff;">
                    <i class="ri-information-line mr-2" style="font-size: 1.5rem;"></i>
                    {{ $t('information') }}
                </div>
            </template>
            <template v-slot:CommonModalContent>
                <div class="text-center py-3 d-flex flex-column align-items-center">
                    <div class="font-weight-bold text-dark mb-4" style="font-size: 1.1rem; line-height: 1.6;">
                        {{ $t('preview_btn_info') }}
                    </div>
                    <b-button
                        variant="info"
                        class="font-weight-bold shadow-sm px-5 text-white rounded-pill py-2"
                        style="background-color: #00b0ff; border: 0;"
                        @click="goToSummary"
                    >
                        {{ $t('ok').toUpperCase() }}
                    </b-button>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
import VisitorStudentApplicationServices from "@/services/VisitorStudentApplicationServices";
import ParameterService from "@/services/ParameterService";
import CommonModal from "@/components/elements/CommonModal.vue";
import qs from "qs";

export default {
    components: { CommonModal },
    props: {
        programLevel: {
            type: String,
            default: 'l'
        },
        serverDocuments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            parameterItems: [],
            requiredDocuments: []
        }
    },
    computed: {
        documentConfig() {
            const level = (this.programLevel || '').toUpperCase();
            const configs = {
                'L':  { required: ['identity_document'], optional: [] },
                'OL': { required: ['identity_document'], optional: [] },
                'H':  { required: ['identity_document'], optional: [] },
                'YL': {
                    required: ['identity_document', 'bachelor_diploma', 'bachelor_transcript'],
                    optional: ['ales_certificate', 'language_certificate']
                },
                'D': {
                    required: ['identity_document', 'bachelor_diploma', 'bachelor_transcript', 'graduate_diploma', 'graduate_transcript'],
                    optional: ['ales_certificate', 'language_certificate']
                }
            };
            return configs[level] || { required: ['identity_document'], optional: [] };
        }
    },
    watch: {
        parameterItems() {
            this.buildRequiredDocuments();
        },
        programLevel() {
            this.buildRequiredDocuments();
        }
    },
    created() {
        this.loadParameterItems();
    },
    methods: {
        buildRequiredDocuments() {
            const { required, optional } = this.documentConfig;
            const allCodes = [...required, ...optional];
            this.requiredDocuments = this.parameterItems
                .filter(item => allCodes.includes(item.code))
                .sort((a, b) => allCodes.indexOf(a.code) - allCodes.indexOf(b.code))
                .map(item => {
                    const uploaded = this.serverDocuments.find(d => d.document_type === item.code);
                    return {
                        code: item.code,
                        required: required.includes(item.code),
                        name: item.name,
                        files: [],
                        alreadyUploaded: !!(uploaded && uploaded.status === 'uploaded')
                    };
                });
        },
        async loadParameterItems() {
            try {
                const config = {
                    params: { parameter_code: 'visitor_student_application_documents', sort: 'name', limit: -1 },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };
                const res = await ParameterService.getItemsPublic(config);
                const data = res.data.data;
                this.parameterItems = Array.isArray(data) ? data : (data?.items || []);
            } catch (e) {
                this.parameterItems = [];
            }
        },
        onFileChange(event, doc) {
            const files = Array.from(event.target.files);
            if (files.length > 0) {
                this.$set(doc, 'files', files);
            }
        },
        async submitAndNext() {
            const docsToUpload = this.requiredDocuments.filter(doc => doc.files.length > 0);

            const missingRequired = this.requiredDocuments.filter(doc => doc.required && doc.files.length === 0 && !doc.alreadyUploaded);
            if (missingRequired.length > 0) {
                this.$toast.error(this.$t('please_upload_required_documents_error'));
                return;
            }

            const hasAnyUploaded = this.requiredDocuments.some(doc => doc.alreadyUploaded || doc.files.length > 0);
            if (!hasAnyUploaded) {
                this.$toast.error(this.$t('please_upload_required_documents_error'));
                return;
            }

            if (docsToUpload.length === 0) {
                this.$emit('next', { documents: this.requiredDocuments });
                return;
            }

            this.loading = true;
            try {
                const credentials = this.$store.getters['VisitorStudentApplicationStore/getCredentials'];

                const formData = new FormData();
                if (credentials.national_id) {
                    formData.append('national_id', credentials.national_id);
                } else {
                    formData.append('passport_number', credentials.passport_number);
                }
                formData.append('pin', credentials.pin);

                docsToUpload.forEach(doc => {
                    doc.files.forEach(file => {
                        formData.append('document_types[]', doc.code);
                        formData.append('files[]', file);
                    });
                });

                await VisitorStudentApplicationServices.addDocument(formData);
                this.$refs.successModal.$refs.commonModal.show()
            } catch (error) {
                this.showErrors(error);
            } finally {
                this.loading = false;
            }
        },
        goToSummary() {
            this.$refs.successModal.$refs.commonModal.hide()
            this.$emit('next', { documents: this.requiredDocuments });
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.last-border-0:last-child {
    border-bottom: 0 !important;
}
</style>
